import useBusEvent from 'hooks/useBusEvent';
import useQueryParams from 'hooks/useQueryParams';
import { CallbackBusType, StandardEventTypes } from 'modules/bus';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UploadedDataRoomNode } from 'router/subrouters/DataRoom/models/DataRoom';
import { DataRoomMember } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import DataRoomUploadContext from 'router/subrouters/DataRoom/providers/DataRoomUpload/DataRoomUpload.context';
import { getIsNodeFile, sortDataRoomNodes } from 'utils';

export default function useCurrentlyDisplayedNodes() {
  const [currentlyDisplayedNodes, setCurrentlyDisplayedNodes] = useState<
    DataRoomNode[]
  >([]);
  const [buySidePreviewMember, setBuySidePreviewMember] =
    useState<DataRoomMember>(null);

  const { isUploading } = useContext(DataRoomUploadContext);

  const { documentId } = useParams();

  const {
    params: { sort },
  } = useQueryParams();

  const newNodeAdded = useCallback(
    ({ payload }: CallbackBusType<UploadedDataRoomNode>) => {
      if (buySidePreviewMember) return;

      const { dataRoomNode, removedDataRoomNodeId } = payload;

      if (dataRoomNode.parentId !== documentId) return;

      setCurrentlyDisplayedNodes((prev) => {
        const updatedFiles = (() => {
          const isFile = getIsNodeFile(dataRoomNode);

          if (isFile) {
            if (!removedDataRoomNodeId) {
              return [
                ...prev.filter(({ _id }) => _id !== dataRoomNode._id),
                dataRoomNode,
              ];
            }

            return [
              ...prev.map((node) =>
                node._id !== removedDataRoomNodeId ? node : dataRoomNode,
              ),
            ];
          }

          return [
            dataRoomNode,
            ...prev.filter(
              (node) =>
                ![removedDataRoomNodeId, dataRoomNode._id].includes(node._id),
            ),
          ];
        })();

        return sortDataRoomNodes(updatedFiles, sort);
      });
    },
    [buySidePreviewMember, documentId, sort],
  );

  useBusEvent(StandardEventTypes.DATA_ROOM_NODE_ADDED, newNodeAdded);

  useEffect(() => {
    if (isUploading && buySidePreviewMember) {
      setBuySidePreviewMember(null);
    }
  }, [buySidePreviewMember, isUploading]);

  return {
    currentlyDisplayedNodes,
    setCurrentlyDisplayedNodes,
    buySidePreviewMember,
    setBuySidePreviewMember,
  };
}
